import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {useEffect, useState, useMemo, useCallback} from "react";
import {observer} from "mobx-react";
import moment from "moment";
import Axios from "axios";
import {DataTable} from "components/dataTable/index.jsx";
import {DateTimeFilter} from "components/dataTable/columnFilter.jsx";
import formatters from "helpers/formatters.jsx";
import Products from "./products";
import DisneyBalance from "./balance";

export default observer(function Disney(props) {
  const appDataStore = useAppDataStore();
  const [activeTab, setActiveTab] = useState('sales');
  const [summary, setSummary] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showNoveltyModal, setShowNoveltyModal] = useState(false);
  const [noveltyModalMode, setNoveltyModalMode] = useState('create'); // 'create' o 'view'
  const [existingNovelty, setExistingNovelty] = useState(null);
  const [noveltyForm, setNoveltyForm] = useState({
    type: '',
    description: '',
    evidence: null
  });
  const [reportingNovelty, setReportingNovelty] = useState(false);
  const [showResendEmailModal, setShowResendEmailModal] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Nueva venta
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [saleProducts, setSaleProducts] = useState([]);
  const [loadingSaleProducts, setLoadingSaleProducts] = useState(false);
  const [saleForm, setSaleForm] = useState({productId: '', customerName: '', email: '', reference: ''});
  const [makingSale, setMakingSale] = useState(false);
  const [saleResult, setSaleResult] = useState(null);

  // Definir funciones primero antes del useMemo
  const handleResendEmail = useCallback(async (sale) => {
    setSelectedSale(sale);
    setResendEmail(sale.email || '');
    setShowResendEmailModal(true);
  }, []);

  const submitResendEmail = async () => {
    try {
      if (!resendEmail || !resendEmail.trim()) {
        alert('Por favor ingresa un correo electrónico');
        return;
      }

      setSendingEmail(true);
      await appDataStore.post("/sales/resend-email", {
        saleId: selectedSale.id,
        alternativeMail: resendEmail.trim()
      });

      alert('Correo reenviado exitosamente');
      setShowResendEmailModal(false);
      setResendEmail('');
      setSelectedSale(null);
    } catch (err) {
      console.error("Error al reenviar correo:", err);
      alert("Error al reenviar correo: " + err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleReportNovelty = useCallback(async (sale) => {
    setSelectedSale(sale);

    if (sale.novelty) {
      // Modo view: obtener el reporte existente
      try {
        const response = await appDataStore.post("/sales/get-novelty", {
          saleId: sale.id
        });
        setExistingNovelty(response.novelty);
        setNoveltyModalMode('view');
      } catch (err) {
        console.error("Error al obtener novedad:", err);
        alert("Error al obtener el reporte de novedad");
        return;
      }
    } else {
      // Modo create: formulario vacío para reportar
      setNoveltyForm({type: '', description: '', evidence: null});
      setNoveltyModalMode('create');
      setExistingNovelty(null);
    }

    setShowNoveltyModal(true);
  }, [appDataStore]);

  const handleNoveltyFormChange = (e) => {
    const {name, value} = e.target;
    setNoveltyForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEvidenceChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setNoveltyForm(prev => ({
          ...prev,
          evidence: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const submitNovelty = async () => {
    try {
      if (!noveltyForm.type || !noveltyForm.description.trim()) {
        alert('Por favor completa todos los campos');
        return;
      }

      setReportingNovelty(true);
      await appDataStore.post("/sales/report-novelty", {
        saleId: selectedSale.id,
        noveltyType: noveltyForm.type,
        description: noveltyForm.description.trim(),
        evidence: noveltyForm.evidence
      });

      alert('Novedad reportada exitosamente');
      setShowNoveltyModal(false);
      setNoveltyForm({type: '', description: '', evidence: null});
      setSelectedSale(null);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al reportar novedad:", err);
      alert("Error al reportar novedad: " + err.message);
    } finally {
      setReportingNovelty(false);
    }
  };

  // Filtro personalizado para Estado - basado en StatusFilter pero con valores de Disney
  const DisneyStatusFilter = ({column}) => {
    const [value, setValue] = useState('');

    useEffect(() => {
      if (value) {
        column.setFilterValue([{key: column.id, operator: 'eq', value}]);
      }
      if (!value) {
        column.setFilterValue([]);
      }
    }, [value, column]);

    return (
      <select
        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="">Todas</option>
        <option value="success">Exitosa</option>
        <option value="pending">Pendiente</option>
        <option value="error">Fallida</option>
      </select>
    );
  };

  const NoveltyFilter = ({column}) => {
    const [value, setValue] = useState('');
    useEffect(() => {
      if (value === '1') column.setFilterValue([{key: 'novelty', operator: 'eq', value: 1}]);
      else if (value === '0') column.setFilterValue([{key: 'novelty', operator: 'eq', value: 0}]);
      else column.setFilterValue([]);
    }, [value]);
    return (
      <select
        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="">Todas</option>
        <option value="1">Con novedad</option>
        <option value="0">Sin novedad</option>
      </select>
    );
  };

  const columns = useMemo(() => ([
    {
      accessorKey: 'transactionId',
      header: 'ID',
    },
    {
      accessorKey: 'reference',
      header: 'Referencia',
    },
    {
      accessorKey: 'customerName',
      header: 'Cliente',
    },
    {
      accessorKey: 'balanceBefore',
      header: 'Saldo anterior',
      cell: ({cell}) => formatters.cash(cell.getValue()),
    },
    {
      accessorKey: 'balanceAfter',
      header: 'Saldo final',
      cell: ({cell}) => formatters.cash(cell.getValue()),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({cell}) => {
        const email = cell.getValue();
        return email ? email.substring(0, 25) + (email.length > 25 ? "..." : "") : "-";
      }
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({cell}) => formatters.cash(cell.getValue()),
    },
    {
      accessorKey: 'product',
      header: 'Producto',
      opFilter: 'eq',
    },
    {
      accessorKey: 'result',
      header: 'Estado',
      opFilter: 'eq',
      cell: ({cell}) => {
        const result = cell.getValue();
        const statusClass = result === "success" ? "bg-green-100 text-green-800" :
          result === "pending" ? "bg-yellow-100 text-yellow-800" :
            result === "error" ? "bg-red-100 text-red-800" :
              "bg-gray-100 text-gray-800";
        return <span className={`px-2 py-1 rounded text-xs font-medium ${statusClass}`}>{result}</span>;
      },
      customFilter: (column) => <DisneyStatusFilter column={column}/>
    },
    {
      accessorKey: 'createdAt',
      header: 'Fecha/Hora',
      customFilter: (column, table) => <DateTimeFilter column={column} table={table}/>,
      cell: ({cell}) => moment(cell.getValue()).format('YYYY-MM-DD HH:mm:ss'),
      exportFormat: (row) => moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      accessorKey: 'novelty',
      header: 'Novedad',
      enableColumnFilter: true,
      cell: ({row, cell}) => {
        if (!cell.getValue()) {
          return (
            <button
              onClick={() => handleReportNovelty(row.original)}
              className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium hover:bg-gray-200 border border-gray-300 whitespace-nowrap"
            >
              Reportar
            </button>
          );
        }
        const status = row.original.SalesNovelty?.status;
        if (status === 'approved') return <button onClick={() => handleReportNovelty(row.original)} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium hover:bg-green-200 border border-green-300 whitespace-nowrap">✓ Aprobada</button>;
        if (status === 'rejected') return <button onClick={() => handleReportNovelty(row.original)} className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium hover:bg-red-200 border border-red-300 whitespace-nowrap">✗ Rechazada</button>;
        return <button onClick={() => handleReportNovelty(row.original)} className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium hover:bg-yellow-200 border border-yellow-300 whitespace-nowrap">⚠ Pendiente</button>;
      },
      exportFormat: (row) => row.novelty ? 'Sí' : 'No',
      customFilter: (column) => <NoveltyFilter column={column}/>
    },
    {
      id: 'actions',
      header: 'Acciones',
      enableColumnFilter: false,
      cell: ({row}) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleResendEmail(row.original)}
            className="px-3 py-1 rounded text-xs font-medium bg-green-600 text-white hover:bg-green-700"
          >
            Reenviar Correo
          </button>
        </div>
      )
    }
  ]), [handleReportNovelty, handleResendEmail]);

  const tabs = [
    {id: 'sales', label: 'Ventas', icon: '📊'},
    {id: 'products', label: 'Productos', icon: '📦'},
    {id: 'balance', label: 'Mi Saldo', icon: '💰'}
  ];

  // Cargar resumen y refrescar tabla al cambiar de tab
  useEffect(() => {
    if (activeTab === 'sales') {
      loadSummary();
      setRefreshKey(prev => prev + 1);
    }
  }, [activeTab]);

  const loadSummary = async () => {
    try {
      const data = await appDataStore.post("/sales/summary", {});
      setSummary(data);
    } catch (err) {
      console.error("Error al cargar resumen:", err);
    }
  };

  const openSaleModal = async () => {
    setSaleResult(null);
    setSaleForm({
      productId: '',
      customerName: '',
      email: '',
      reference: `REF-${Date.now()}`,
    });
    setShowSaleModal(true);
    if (saleProducts.length === 0) {
      setLoadingSaleProducts(true);
      try {
        const res = await appDataStore.post('/products/list', {limit: 100, offset: 0});
        setSaleProducts((res.products || []).filter(p => p.enabled === '1' || p.enabled === 1 || p.enabled === true));
      } catch (err) {
        console.error('Error cargando productos:', err);
      } finally {
        setLoadingSaleProducts(false);
      }
    }
  };

  const submitSale = async () => {
    const {productId, customerName, email, reference} = saleForm;
    if (!productId) return alert('Selecciona un producto');
    if (!customerName.trim()) return alert('Ingresa el nombre del cliente');
    if (!email.trim()) return alert('Ingresa el correo del cliente');
    if (!reference.trim()) return alert('Ingresa la referencia');
    try {
      setMakingSale(true);
      const res = await Axios.post('/api/v2/entertainment/buy', {
        data: {
          reference: reference.trim(),
          customerName: customerName.trim(),
          email: email.trim(),
          productId: parseInt(productId),
        }
      }, {
        headers: {authorization: `Token ${appDataStore.user?.token}`},
      });
      setSaleResult({success: true, data: res.data.data});
      setRefreshKey(prev => prev + 1);
      loadSummary();
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setSaleResult({success: false, error: msg});
    } finally {
      setMakingSale(false);
    }
  };

  const dataFetch = useCallback(async (
    records,
    currentPage,
    order,
    search,
    filter
  ) => {
    try {
      const requestData = {
        records,
        currentPage,
        order,
        search,
        filter,
        attributes: ['id', 'amount', 'balanceBefore', 'balanceAfter', 'result', 'error', 'transactionId', 'reference', 'phoneNumber', 'email', 'customerName', 'novelty', 'product', 'createdAt'],
        searchAttributes: ['transactionId', 'reference', 'customerName', 'email', 'phoneNumber'],
        include: [{model: 'SalesNovelty', attributes: ['status'], required: false}]
      };

      const response = await appDataStore.post("/app/datasource/Sale", requestData);

      return {
        data: response.data || [],
        totalRows: response.totalRows || 0,
        totalPages: response.totalPages || 1
      };
    } catch (err) {
      console.error("Error al cargar ventas:", err);
      return {
        data: [],
        totalRows: 0,
        totalPages: 1
      };
    }
  }, [appDataStore]);

  return (
    <>
      <div className="p-2 text-black">
        {/* Tabs Navigation */}
        <div className="flex border-b border-gray-300 mb-6 gap-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium transition-all border-b-2 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              <span className="text-lg mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="w-full">
          {activeTab === 'sales' && (
            <div className="animate-fadeIn">
              {/* Resumen de Ventas */}
              {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap- mb-6">
                  <div className="border border-gray-300 rounded-lg p-4 m-2 bg-blue-50">
                    <p className="text-sm text-gray-600">Total de Ventas</p>
                    <p className="text-3xl font-bold text-blue-600">{summary.totalSales || 0}</p>
                  </div>
                  <div className="border border-gray-300 rounded-lg p-4 m-2 bg-green-50">
                    <p className="text-sm text-gray-600">Valor ventas</p>
                    <p className="text-3xl font-bold text-green-600">{formatters.cash(summary.totalRevenue || 0)}</p>
                  </div>

                </div>
              )}

              {/* Botón nueva venta */}
              <div className="flex justify-end mb-3">
                <button onClick={openSaleModal}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium text-sm">
                  + Nueva Venta
                </button>
              </div>

              {/* DataTable de Ventas */}
              <DataTable
                exportFileName={`ventas_disney_${moment().format('DDMMYY_HHmm')}`}
                columns={columns}
                dataFetch={dataFetch}
                defaultSorting={[{id: 'createdAt', desc: true}]}
                refreshDependencies={refreshKey}
              />
            </div>
          )}

          {activeTab === 'products' && (
            <div className="animate-fadeIn">
              <Products {...props} />
            </div>
          )}

          {activeTab === 'balance' && (
            <div className="animate-fadeIn">
              <DisneyBalance {...props} />
            </div>
          )}
        </div>
      </div>

      {/* Modal Reportar/Ver Novedad */}
      {showNoveltyModal && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Encabezado */}
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {noveltyModalMode === 'view' ? 'Detalle de Novedad Reportada' : 'Reportar Novedad en Venta'}
            </h3>

            {/* Información de la Venta */}
            <div className="mb-4 p-3 bg-gray-100 rounded">
              <p className="text-sm text-gray-600"><strong>ID Transacción:</strong> {selectedSale.transactionId}</p>
              <p className="text-sm text-gray-600"><strong>Referencia:</strong> {selectedSale.reference}</p>
              <p className="text-sm text-gray-600"><strong>Cliente:</strong> {selectedSale.customerName}</p>
              <p className="text-sm text-gray-600"><strong>Monto:</strong> {formatters.cash(selectedSale.amount)}</p>
              <p className="text-sm text-gray-600"><strong>Producto:</strong> {selectedSale.product}</p>
            </div>

            {noveltyModalMode === 'view' && existingNovelty && (
              // Modo VIEW: Mostrar reporte existente
              <div className="space-y-4">
                {/* Tipo de Novedad */}
                <div className="p-3 bg-blue-50 rounded border border-blue-200">
                  <p className="text-sm text-gray-600 mb-1"><strong>Tipo de Novedad:</strong></p>
                  <p className="text-sm text-gray-800">{existingNovelty.noveltyType}</p>
                </div>

                {/* Descripción */}
                <div className="p-3 bg-blue-50 rounded border border-blue-200">
                  <p className="text-sm text-gray-600 mb-1"><strong>Descripción:</strong></p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{existingNovelty.description}</p>
                </div>

                {/* Estado */}
                <div className="p-3 bg-blue-50 rounded border border-blue-200">
                  <p className="text-sm text-gray-600 mb-1"><strong>Estado:</strong></p>
                  <p className={`text-sm font-semibold ${
                    existingNovelty.status === 'approved' ? 'text-green-700' :
                      existingNovelty.status === 'rejected' ? 'text-red-700' :
                        'text-yellow-700'
                  }`}>
                    {existingNovelty.status === 'pending' ? 'Pendiente' :
                      existingNovelty.status === 'approved' ? 'Aprobada' :
                        'Rechazada'}
                  </p>
                </div>

                {/* Evidencia */}
                {existingNovelty.evidence && (
                  <div className="p-3 bg-blue-50 rounded border border-blue-200">
                    <p className="text-sm text-gray-600 mb-2"><strong>Evidencia:</strong></p>
                    <a
                      href={existingNovelty.evidence}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      Ver archivo
                    </a>
                  </div>
                )}

                {/* Solución */}
                {existingNovelty.solution && (
                  <div className="p-3 bg-green-50 rounded border border-green-200">
                    <p className="text-sm text-gray-600 mb-1"><strong>Solución Aplicada:</strong></p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{existingNovelty.solution}</p>
                  </div>
                )}

                {/* Razón de Rechazo */}
                {existingNovelty.rejectionReason && (
                  <div className="p-3 bg-red-50 rounded border border-red-200">
                    <p className="text-sm text-gray-600 mb-1"><strong>Motivo del Rechazo:</strong></p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{existingNovelty.rejectionReason}</p>
                  </div>
                )}

                {/* Fechas */}
                <div className="text-xs text-gray-500 p-3 bg-gray-50 rounded">
                  <p>Reportado: {moment(existingNovelty.createdAt).format('YYYY-MM-DD HH:mm:ss')}</p>
                  {existingNovelty.updatedAt && (
                    <p>Actualizado: {moment(existingNovelty.updatedAt).format('YYYY-MM-DD HH:mm:ss')}</p>
                  )}
                </div>
              </div>
            )}

            {noveltyModalMode === 'create' && (
              // Modo CREATE: Formulario para reportar
              <div className="space-y-4">
                {/* Tipo de Novedad */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Novedad *
                  </label>
                  <select
                    name="type"
                    value={noveltyForm.type}
                    onChange={handleNoveltyFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="">Selecciona un tipo</option>
                    <option value="error_monto">Error en Monto</option>
                    <option value="error_saldo">Error en Saldo</option>
                    <option value="cliente_reclamo">Reclamo del Cliente</option>
                    <option value="fallo_procesamiento">Fallo en Procesamiento</option>
                    <option value="transaccion_duplicada">Transacción Duplicada</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción de la Novedad *
                  </label>
                  <textarea
                    name="description"
                    value={noveltyForm.description}
                    onChange={handleNoveltyFormChange}
                    placeholder="Describe el problema o novedad encontrada..."
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm h-24 resize-none"
                  />
                </div>

                {/* Evidencia */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Evidencia (Archivo)
                  </label>
                  <input
                    type="file"
                    onChange={handleEvidenceChange}
                    accept="image/*,.pdf,.doc,.docx"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                  {noveltyForm.evidence && (
                    <p className="text-xs text-green-600 mt-2">Archivo seleccionado correctamente</p>
                  )}
                </div>
              </div>
            )}

            {/* Botones */}
            <div className="flex gap-2 mt-6">
              {noveltyModalMode === 'create' && (
                <>
                  <button
                    onClick={submitNovelty}
                    disabled={reportingNovelty || !noveltyForm.type || !noveltyForm.description.trim()}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-400 font-medium"
                  >
                    {reportingNovelty ? 'Reportando...' : 'Reportar Novedad'}
                  </button>
                  <button
                    onClick={() => {
                      setShowNoveltyModal(false);
                      setSelectedSale(null);
                      setNoveltyForm({type: '', description: '', evidence: null});
                    }}
                    disabled={reportingNovelty}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:bg-gray-400 font-medium"
                  >
                    Cancelar
                  </button>
                </>
              )}

              {noveltyModalMode === 'view' && (
                <button
                  onClick={() => {
                    setShowNoveltyModal(false);
                    setSelectedSale(null);
                    setExistingNovelty(null);
                  }}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                >
                  Cerrar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Reenviar Correo */}
      {showResendEmailModal && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Reenviar Correo de Activación
            </h3>

            {/* Información de la Venta */}
            <div className="mb-4 p-3 bg-gray-100 rounded">
              <p className="text-sm text-gray-600"><strong>ID Transacción:</strong> {selectedSale.transactionId}</p>
              <p className="text-sm text-gray-600"><strong>Referencia:</strong> {selectedSale.reference}</p>
              <p className="text-sm text-gray-600"><strong>Cliente:</strong> {selectedSale.customerName}</p>
              <p className="text-sm text-gray-600"><strong>Producto:</strong> {selectedSale.product}</p>
            </div>

            {/* Campo de correo electrónico */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correo Electrónico *
              </label>
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="ejemplo@correo.com"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Por defecto se usa el correo de la venta. Puedes cambiarlo si es necesario.
              </p>
            </div>

            {/* Botones */}
            <div className="flex gap-2">
              <button
                onClick={submitResendEmail}
                disabled={sendingEmail || !resendEmail.trim()}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 font-medium"
              >
                {sendingEmail ? 'Enviando...' : 'Reenviar Correo'}
              </button>
              <button
                onClick={() => {
                  setShowResendEmailModal(false);
                  setSelectedSale(null);
                  setResendEmail('');
                }}
                disabled={sendingEmail}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:bg-gray-400 font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Nueva Venta */}
      {showSaleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Nueva Venta</h3>
              <button onClick={() => setShowSaleModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">×</button>
            </div>

            {saleResult ? (
              <div>
                {saleResult.success ? (
                  <div className="space-y-3">
                    <div className="bg-green-50 border border-green-200 rounded p-3">
                      <p className="text-green-800 font-semibold text-sm mb-2">Venta realizada exitosamente</p>
                      <p className="text-xs text-gray-600"><span className="font-medium">ID:</span> {saleResult.data.id}</p>
                      <p className="text-xs text-gray-600"><span className="font-medium">Referencia:</span> {saleResult.data.reference}</p>
                    </div>
                    {saleResult.data.activationUrl && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                        <p className="text-xs text-gray-600 font-medium mb-1">URL de activación:</p>
                        <a href={saleResult.data.activationUrl} target="_blank" rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-xs break-all">
                          {saleResult.data.activationUrl}
                        </a>
                      </div>
                    )}
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => { setSaleResult(null); setSaleForm({productId: '', customerName: '', email: '', reference: `REF-${Date.now()}`}); }}
                        className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium text-sm">
                        Nueva venta
                      </button>
                      <button onClick={() => setShowSaleModal(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 font-medium text-sm">
                        Cerrar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <p className="text-red-800 font-semibold text-sm mb-1">Error al procesar la venta</p>
                      <p className="text-xs text-red-700">{saleResult.error}</p>
                    </div>
                    <button onClick={() => setSaleResult(null)}
                      className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium text-sm">
                      Intentar de nuevo
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Producto *</label>
                  {loadingSaleProducts ? (
                    <div className="text-sm text-gray-500 py-2">Cargando productos...</div>
                  ) : (
                    <select value={saleForm.productId}
                      onChange={e => setSaleForm(prev => ({...prev, productId: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                      <option value="">Selecciona un producto</option>
                      {saleProducts.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} — {formatters.cash(p.price)} ({p.validity} mes{p.validity > 1 ? 'es' : ''})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referencia *</label>
                  <input type="text" value={saleForm.reference}
                    onChange={e => setSaleForm(prev => ({...prev, reference: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del cliente *</label>
                  <input type="text" placeholder="Ej: Juan Pérez" value={saleForm.customerName}
                    onChange={e => setSaleForm(prev => ({...prev, customerName: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Correo del cliente *</label>
                  <input type="email" placeholder="cliente@correo.com" value={saleForm.email}
                    onChange={e => setSaleForm(prev => ({...prev, email: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={submitSale} disabled={makingSale}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded font-medium text-sm">
                    {makingSale ? 'Procesando...' : 'Realizar venta'}
                  </button>
                  <button onClick={() => setShowSaleModal(false)} disabled={makingSale}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 font-medium text-sm">
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }
      `}</style>
    </>
  );
});
